/**
 * Extracts specific query parameters from a URL search string in a type-safe way.
 *
 * This utility helps avoid repetitive `URLSearchParams.get()` calls
 * and ensures strong typing for known query keys.
 *
 * @template T - Union type of allowed query parameter keys
 *
 * @param {string} search - The URL search string (e.g. "?type=QR&from_qr_code=true")
 * @param {readonly T[]} keys - List of query parameter keys to extract
 *
 * @returns {Record<T, string | null>}
 * An object where:
 * - keys are the requested query params
 * - values are either `string` (if present) or `null` (if missing)
 *
 * @example
 * // React Router usage
 * import { useLocation } from 'react-router-dom';
 * import { extractQueryParamsValue } from '@/utils/queryParams';
 *
 * const { search } = useLocation();
 *
 * const { type, from_qr_code } = extractQueryParamsValue(search, [
 *   'type',
 *   'from_qr_code',
 * ] as const);
 *
 * const isFromQrCode = from_qr_code === 'true';
 *
 * @example
 * // Outside React (pure JS usage)
 * const params = extractQueryParamsValue(
 *   '?page=1&sort=asc',
 *   ['page', 'sort'] as const
 * );
 *
 * console.log(params.page); // "1"
 * console.log(params.sort); // "asc"
 *
 * @remarks
 * - All query parameter values are returned as strings or null.
 * - Boolean or number conversion must be handled explicitly by the caller.
 * - Using `as const` ensures proper key inference and strong typing.
 */

export const extractQueryParamsValue = <T extends string>(
  search: string,
  keys: readonly T[],
): Record<T, string | null> => {
  const params = new URLSearchParams(search);

  return keys.reduce(
    (acc, key) => {
      acc[key] = params.get(key);
      return acc;
    },
    {} as Record<T, string | null>,
  );
};
